import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import '../styles/ServicesPage.css';

export const servicePillars = [
  {
    id: 'psychological-mental-health',
    title: 'Psychological & Mental Health Support',
    emoji: '🧠',
    pillarClass: 'pillar-psychological',
    tagline: 'Clinical & Emotional Wellbeing',
    accentColor: '#7c4bb9',
    lightBg: '#f6f0ff',
    badgeText: '7 Specialties',
    description: 'Evidence-based psychiatric, psychological, and clinical therapies addressing emotional distress, life transitions, and mental health conditions across all ages.',
    services: [
      {
        id: 'counselling-psychologist',
        title: 'Counselling Psychologist',
        icon: 'fas fa-hands-holding-child',
        tag: 'Everyday Wellbeing',
        description: 'Helps individuals navigate life challenges, emotional distress, and relationship dynamics while building emotional resilience, coping skills, and self-growth.',
        points: [
          'Stress, anxiety and emotional wellbeing',
          'Life transitions, grief & difficult adjustments',
          'Relationships, self-esteem & personal growth',
          'Evidence-based CBT and mindfulness coping'
        ],
        btnText: 'Find Counselling Psychologists',
        tags: ['Stress & Anxiety', 'Life Transitions', 'Self-Esteem', 'Relationships', 'CBT'],
        exploreQuery: 'Counselling Psychologist'
      },
      {
        id: 'clinical-psychologist',
        title: 'Clinical Psychologist',
        icon: 'fas fa-brain',
        tag: 'Clinical Diagnosis',
        description: 'Specializes in comprehensive psychological assessments, clinical diagnoses, and specialized psychotherapeutic interventions for moderate to severe mental health conditions.',
        points: [
          'In-depth psychological assessment & diagnostic tests',
          'Structured psychotherapy for mood & anxiety disorders',
          'Managing complex and persistent mental health conditions',
          'Cognitive, emotional and behavioural interventions'
        ],
        btnText: 'Find Clinical Psychologists',
        tags: ['Clinical Assessment', 'Psychotherapy', 'Severe Concerns', 'Diagnostic Tests', 'Trauma'],
        exploreQuery: 'Clinical Psychologist'
      },
      {
        id: 'psychiatrist',
        title: 'Psychiatrist',
        icon: 'fas fa-user-md',
        tag: 'Medical Doctor',
        description: 'Medical doctors specializing in psychiatric medicine who provide psychiatric evaluations, medical assessments, and clinical prescription management when needed.',
        points: [
          'Medical & psychiatric evaluations and diagnosis',
          'Prescription & medication management plans',
          'Continuous symptom tracking and therapeutic adjustment',
          'Comprehensive biological & psychiatric care'
        ],
        btnText: 'Find Psychiatrists',
        tags: ['Medical Evaluation', 'Medication Management', 'Psychiatric Diagnosis', 'Mood Disorders'],
        exploreQuery: 'Psychiatrist'
      },
      {
        id: 'child-psychologist',
        title: 'Child & Adolescent Psychologist',
        icon: 'fas fa-child-reaching',
        tag: 'Youth & Teens',
        description: 'Dedicated to supporting the emotional, behavioural, and social development of children and teenagers within a nurturing, developmentally attuned environment.',
        points: [
          'Childhood anxiety, emotional outbursts & phobias',
          'Adolescent identity, school pressure & social anxiety',
          'Parenting guidance & parent-child communication',
          'Play therapy, behavioural therapy & emotional regulation'
        ],
        btnText: 'Find Child Psychologists',
        tags: ['Child & Teen', 'School Challenges', 'Parenting Support', 'Behavioral Needs', 'Play Therapy'],
        exploreQuery: 'Child Psychologist'
      },
      {
        id: 'rehabilitation-psychologist',
        title: 'Rehabilitation Psychologist',
        icon: 'fas fa-wheelchair-move',
        tag: 'Adaptive Living',
        description: 'Supports individuals and families coping with cognitive, physical, or developmental disabilities to maximize functional independence, adaptation, and life fulfillment.',
        points: [
          'Neurodevelopmental & intellectual disabilities',
          'Autism spectrum & developmental conditions',
          'Adaptive skills, functional independence & daily living',
          'Caregiver guidance and psycho-social rehabilitation'
        ],
        btnText: 'Find Rehabilitation Psychologists',
        tags: ['Neurodiversity', 'Autism Support', 'Adaptive Living', 'Rehabilitation', 'Caregivers'],
        exploreQuery: 'Rehabilitation Psychologist'
      },
      {
        id: 'geriatric-psychologist',
        title: 'Geriatric Psychologist',
        icon: 'fas fa-person-cane',
        tag: 'Elderly Care',
        description: 'Focuses on the mental, emotional, and cognitive wellbeing of older adults, addressing the unique biological, social, and psychological transitions of later life.',
        points: [
          'Emotional wellbeing & adjustment in later life',
          'Cognitive health, memory decline & dementia support',
          'Coping with illness, loss, isolation & bereavement',
          'Caregiver burnout relief and family counseling'
        ],
        btnText: 'Find Geriatric Psychologists',
        tags: ['Senior Care', 'Ageing & Memory', 'Caregiver Support', 'Grief & Loss', 'Late-life Wellbeing'],
        exploreQuery: 'Geriatric Psychologist'
      },
      {
        id: 'psycho-oncologist',
        title: 'Psycho-Oncologist',
        icon: 'fas fa-ribbon',
        tag: 'Cancer & Illness',
        description: 'Provides compassionate psychological care for patients, survivors, and families navigating cancer diagnosis, treatment cycles, and medical recovery.',
        points: [
          'Emotional coping during diagnosis & chemotherapy',
          'Managing fear of recurrence, scanxiety & pain distress',
          'Quality of life, body image & existential wellbeing',
          'Family, partner, and caregiver support sessions'
        ],
        btnText: 'Find Psycho-Oncologists',
        tags: ['Cancer Support', 'Caregiver Relief', 'Scanxiety', 'Medical Trauma', 'Quality of Life'],
        exploreQuery: 'Psycho-Oncologist'
      }
    ]
  },
  {
    id: 'development-functional',
    title: 'Development & Functional Support',
    emoji: '🌱',
    pillarClass: 'pillar-development',
    tagline: 'Functional, Motor & Learning Growth',
    accentColor: '#059669',
    lightBg: '#ecfdf5',
    badgeText: '3 Specialties',
    description: 'Specialized interventions and therapies that empower learning capabilities, independent daily living skills, sensory integration, and speech communication.',
    services: [
      {
        id: 'special-educator',
        title: 'Special Educator',
        icon: 'fas fa-graduation-cap',
        tag: 'Learning Support',
        description: 'Designs individualized educational strategies and remediation plans for children and students with dyslexia, dyscalculia, ADHD, and unique learning differences.',
        points: [
          'Individualized Education Plans (IEPs)',
          'Reading, writing, maths and comprehension remediation',
          'Tailored multisensory learning techniques',
          'Collaboration with schools and parents'
        ],
        btnText: 'Find Special Educators',
        tags: ['Learning Disabilities', 'ADHD & Dyslexia', 'IEP Planning', 'Academic Growth', 'Remediation'],
        exploreQuery: 'Special Educator'
      },
      {
        id: 'occupational-therapist',
        title: 'Occupational Therapist',
        icon: 'fas fa-hand-holding-medical',
        tag: 'Sensory & Motor',
        description: 'Empowers children and adults to develop, regain, or adapt sensory processing, fine motor skills, and functional independence for school, work, and everyday life.',
        points: [
          'Sensory integration & sensory processing difficulties',
          'Fine motor, handwriting & gross motor coordination',
          'Daily self-care & independent living skills',
          'Environmental adaptations for school and home'
        ],
        btnText: 'Find Occupational Therapists',
        tags: ['Sensory Processing', 'Fine Motor Skills', 'Daily Independence', 'Coordination', 'OT'],
        exploreQuery: 'Occupational Therapist'
      },
      {
        id: 'speech-therapist',
        title: 'Speech & Language Therapist',
        icon: 'fas fa-comment-dots',
        tag: 'Communication',
        description: 'Assesses and treats speech delays, articulation difficulties, stammering/stuttering, language expression, voice modulation, and swallowing disorders.',
        points: [
          'Speech clarity, articulation & pronunciation',
          'Expressive & receptive language development',
          'Stammering, stuttering & fluency therapy',
          'Voice modulation and social communication skills'
        ],
        btnText: 'Find Speech Therapists',
        tags: ['Speech Clarity', 'Language Milestones', 'Stammering & Fluency', 'Voice Therapy', 'Articulation'],
        exploreQuery: 'Speech Therapist'
      }
    ]
  },
  {
    id: 'performance-work-education',
    title: 'Performance, Work & Education',
    emoji: '🎯',
    pillarClass: 'pillar-performance',
    tagline: 'Peak Potential & Goal Achievement',
    accentColor: '#d97706',
    lightBg: '#fffbeb',
    badgeText: '4 Specialties',
    description: 'Empowering athletes, students, executives, and organizations to conquer performance pressure, optimize career trajectory, and unlock peak fulfillment and leadership.',
    services: [
      {
        id: 'sports-psychologist',
        title: 'Performance & Sports Psychologist',
        icon: 'fas fa-person-running',
        tag: 'Athlete Mindset',
        description: 'Applies psychological science to help athletes, performers, and competitors master mental grit, focus, and composure under extreme pressure.',
        points: [
          'Mental preparation, visualization & pre-game routines',
          'Overcoming competition anxiety & choking under pressure',
          'Concentration, emotional control & mental stamina',
          'Injury rehabilitation mindset & athletic burnout recovery'
        ],
        btnText: 'Find Sports Psychologists',
        tags: ['Athlete Mindset', 'Performance Anxiety', 'Focus & Mental Grit', 'Peak State', 'Athletics'],
        exploreQuery: 'Sports Psychologist'
      },
      {
        id: 'career-counsellor',
        title: 'Career Counsellor',
        icon: 'fas fa-compass',
        tag: 'Career Roadmaps',
        description: 'Provides structured aptitude assessment, career guidance, stream selection, and strategic roadmap planning for students and working professionals.',
        points: [
          'Psychometric aptitude, interest & personality profiling',
          'Subject choices, college admissions & career pathing',
          'Mid-career pivots, transitions & skill alignment',
          'Resume readiness, interview confidence & career clarity'
        ],
        btnText: 'Find Career Counsellors',
        tags: ['Career Roadmaps', 'Aptitude Profiling', 'Subject Selection', 'Career Pivots', 'Mentorship'],
        exploreQuery: 'Career Counsellor'
      },
      {
        id: 'organisational-psychologist',
        title: 'Organisational Psychologist',
        icon: 'fas fa-briefcase',
        tag: 'Workplace & Culture',
        description: 'Applies psychological principles to workplace ecosystems to optimize employee wellbeing, leadership capability, team synergy, and burnout prevention.',
        points: [
          'Corporate wellness programs & stress reduction audits',
          'Leadership coaching & emotional intelligence at work',
          'Team conflict resolution & psychological safety',
          'Workplace culture, employee motivation & retention'
        ],
        btnText: 'Find Workplace Psychologists',
        tags: ['Corporate Wellness', 'Leadership Coaching', 'Team Dynamics', 'Burnout Prevention', 'HR'],
        exploreQuery: 'Organisational Psychologist'
      },
      {
        id: 'coach',
        title: 'Life & Executive Coach',
        icon: 'fas fa-bullseye',
        tag: 'Action & Growth',
        description: 'Partners with individuals and leaders to clarify ambitious goals, establish accountability habits, overcome self-limiting beliefs, and accelerate purposeful action.',
        points: [
          'Goal setting, strategic clarity & execution roadmaps',
          'Breaking procrastination & building high-impact habits',
          'Executive presence, communication & leadership mastery',
          'Work-life integration & personal transformation'
        ],
        btnText: 'Find Life & Executive Coaches',
        tags: ['Goal Clarity', 'Accountability', 'Executive Coaching', 'Habit Mastery', 'Life Direction'],
        exploreQuery: 'Coach'
      }
    ]
  }
];

const quickSearchChips = [
  'Stress & Anxiety',
  'Clinical Psychologists',
  'Psychiatrists',
  'Child & Teen',
  'Speech Therapy',
  'Occupational Therapy',
  'Special Education',
  'Sports Psychology',
  'Career Guidance'
];

function ServicesPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Interactive collapsible state: track which pillars are currently open
  const [expandedPillars, setExpandedPillars] = useState({
    'psychological-mental-health': true,
    'development-functional': false,
    'performance-work-education': false
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const tabParam = searchParams.get('tab');
    if (tabParam && ['psychological-mental-health', 'development-functional', 'performance-work-education'].includes(tabParam)) {
      setActiveTab(tabParam);
      setExpandedPillars((prev) => ({ ...prev, [tabParam]: true }));
    }
  }, [searchParams]);

  // When search term changes or activeTab changes, auto-open matching pillars
  useEffect(() => {
    if (searchTerm.trim() !== '') {
      setExpandedPillars({
        'psychological-mental-health': true,
        'development-functional': true,
        'performance-work-education': true
      });
    } else if (activeTab !== 'all') {
      setExpandedPillars((prev) => ({ ...prev, [activeTab]: true }));
    }
  }, [searchTerm, activeTab]);

  const togglePillar = (pillarId) => {
    setExpandedPillars((prev) => ({
      ...prev,
      [pillarId]: !prev[pillarId]
    }));
  };

  const handleExpandAll = () => {
    setExpandedPillars({
      'psychological-mental-health': true,
      'development-functional': true,
      'performance-work-education': true
    });
  };

  const handleCollapseAll = () => {
    setExpandedPillars({
      'psychological-mental-health': false,
      'development-functional': false,
      'performance-work-education': false
    });
  };

  const handleExplore = (service) => {
    navigate(`/therapists?search=${encodeURIComponent(service.exploreQuery || service.title)}`);
  };

  const handleQuickChip = (chip) => {
    setSearchTerm(chip);
  };

  // Filtered categories based on activeTab and searchTerm
  const filteredPillars = useMemo(() => {
    return servicePillars
      .filter((pillar) => {
        if (activeTab === 'all') return true;
        return pillar.id === activeTab;
      })
      .map((pillar) => {
        if (!searchTerm.trim()) return pillar;

        const term = searchTerm.toLowerCase();
        const matchedServices = pillar.services.filter((s) => {
          const matchTitle = s.title.toLowerCase().includes(term);
          const matchDesc = s.description.toLowerCase().includes(term);
          const matchPoints = s.points.some((p) => p.toLowerCase().includes(term));
          const matchTags = s.tags.some((t) => t.toLowerCase().includes(term));
          const matchTag = s.tag.toLowerCase().includes(term);
          return matchTitle || matchDesc || matchPoints || matchTags || matchTag;
        });

        return {
          ...pillar,
          services: matchedServices
        };
      })
      .filter((pillar) => pillar.services.length > 0);
  }, [activeTab, searchTerm]);

  const totalServicesCount = servicePillars.reduce((acc, p) => acc + p.services.length, 0);

  return (
    <div className="pm-services-hub">
      {/* Dynamic Background Glowing Mesh */}
      <div className="pm-services-glow-mesh">
        <div className="glow-orb glow-orb-1"></div>
        <div className="glow-orb glow-orb-2"></div>
      </div>

      {/* =========================================================
          COMPACT & CLEAN HERO SECTION
          ========================================================= */}
      <header className="pm-services-hero-compact">
        <div className="container">
          <div className="pm-hero-content-box">
            <h1 className="pm-hero-main-title">
              Our <span className="pm-title-gradient">Specialized Services</span>
            </h1>

            {/* Sleek Search Input Bar */}
            <div className="pm-hero-search-wrapper">
              <i className="fas fa-search pm-search-lens-icon"></i>
              <input
                type="text"
                className="pm-hero-search-input"
                placeholder="Search concern, specialty, or treatment (e.g. Anxiety, Autism, Speech, Career)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  type="button"
                  className="pm-search-clear-btn"
                  onClick={() => setSearchTerm('')}
                  title="Clear search"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
            </div>

            {/* Quick Filter Chips */}
            <div className="pm-quick-chips-row">
              <span className="pm-quick-label">Quick filter:</span>
              <div className="pm-chips-list">
                {quickSearchChips.slice(0, 6).map((chip, idx) => (
                  <button
                    key={idx}
                    type="button"
                    className={`pm-quick-chip-btn ${searchTerm.toLowerCase() === chip.toLowerCase() ? 'active' : ''}`}
                    onClick={() => handleQuickChip(chip)}
                  >
                    {chip}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* =========================================================
          STICKY PILLAR NAVIGATION TABS & EXPAND TOGGLE
          ========================================================= */}
      <nav className="pm-sticky-filter-bar">
        <div className="container">
          <div className="pm-filter-tabs-wrapper">
            <div className="pm-tabs-scroll-group">
              <button
                type="button"
                className={`pm-nav-pill-btn ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                <span className="pm-pill-icon">🌟</span>
                <span className="pm-pill-text">All Specialties</span>
                <span className="pm-pill-count">{totalServicesCount}</span>
              </button>

              {servicePillars.map((pillar) => (
                <button
                  key={pillar.id}
                  type="button"
                  className={`pm-nav-pill-btn ${activeTab === pillar.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(pillar.id);
                    setExpandedPillars((prev) => ({ ...prev, [pillar.id]: true }));
                  }}
                >
                  <span className="pm-pill-icon">{pillar.emoji}</span>
                  <span className="pm-pill-text">{pillar.title.split('&')[0]}</span>
                  <span className="pm-pill-count">{pillar.services.length}</span>
                </button>
              ))}
            </div>

            <div className="pm-expand-collapse-actions">
              <button
                type="button"
                className="pm-toggle-all-btn"
                onClick={handleExpandAll}
                title="Expand all categories"
              >
                <i className="fas fa-chevron-down me-1"></i> Expand All
              </button>
              <button
                type="button"
                className="pm-toggle-all-btn"
                onClick={handleCollapseAll}
                title="Collapse all categories"
              >
                <i className="fas fa-chevron-up me-1"></i> Collapse All
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* =========================================================
          SERVICES MAIN LIST WITH CLICK-TO-EXPAND ACCORDION
          ========================================================= */}
      <main className="container pm-services-main-content">
        {filteredPillars.length === 0 ? (
          <div className="pm-empty-results-card">
            <div className="pm-empty-emoji">🔍</div>
            <h3 className="pm-empty-title">No Matching Services Found</h3>
            <p className="pm-empty-text">
              We couldn't find any specialized service matching "<strong>{searchTerm}</strong>". Try clearing your search or explore other keywords like <em>anxiety, child, speech, career</em>.
            </p>
            <button
              type="button"
              className="pm-btn-primary"
              onClick={() => {
                setSearchTerm('');
                setActiveTab('all');
              }}
            >
              <i className="fas fa-rotate-left me-2"></i> Reset Search &amp; Filters
            </button>
          </div>
        ) : (
          filteredPillars.map((pillar) => {
            const isExpanded = !!expandedPillars[pillar.id];

            return (
              <section
                key={pillar.id}
                id={pillar.id}
                className={`pm-pillar-accordion-group ${pillar.pillarClass} ${isExpanded ? 'pillar-is-open' : 'pillar-is-closed'}`}
              >
                {/* Clickable Interactive Main Heading Banner */}
                <div
                  className="pm-pillar-interactive-header"
                  onClick={() => togglePillar(pillar.id)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      togglePillar(pillar.id);
                    }
                  }}
                >
                  <div className="pm-pillar-header-left">
                    <div className="pm-pillar-badge">
                      <span>{pillar.emoji}</span>
                      <span>{pillar.tagline}</span>
                    </div>
                    <div className="pm-pillar-title-row">
                      <h2 className="pm-pillar-main-heading">{pillar.title}</h2>
                      <span className="pm-count-badge">
                        {pillar.services.length} Specialisations
                      </span>
                    </div>
                    <p className="pm-pillar-description">{pillar.description}</p>
                  </div>

                  <div className="pm-pillar-toggle-btn-wrap">
                    <span className="pm-toggle-hint-text">
                      {isExpanded ? 'Click to collapse' : 'Click to view services'}
                    </span>
                    <div className={`pm-accordion-chevron-circle ${isExpanded ? 'open' : ''}`}>
                      <i className="fas fa-chevron-down"></i>
                    </div>
                  </div>
                </div>

                {/* Sub-Data: Animated Collapsible Cards Grid */}
                {isExpanded && (
                  <div className="pm-pillar-subdata-drawer">
                    <div className="pm-cards-responsive-grid">
                      {pillar.services.map((service) => (
                        <article key={service.id} className="pm-modern-service-card">
                          <div className="pm-card-top-shimmer"></div>

                          {/* Card Header */}
                          <div className="pm-card-head">
                            <div className="pm-card-icon-container">
                              <i className={service.icon}></i>
                            </div>
                            <span className="pm-card-badge-tag">{service.tag}</span>
                          </div>

                          {/* Card Content */}
                          <div className="pm-card-body-content">
                            <h3 className="pm-card-title-text">{service.title}</h3>
                            <p className="pm-card-summary-desc">{service.description}</p>

                            <div className="pm-focus-areas-block">
                              <span className="pm-focus-heading">
                                <i className="fas fa-check-circle me-1"></i> Key Focus Areas
                              </span>
                              <ul className="pm-focus-list">
                                {service.points.map((point, pIdx) => (
                                  <li key={pIdx}>
                                    <i className="fas fa-circle-check pm-check-icon"></i>
                                    <span>{point}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>

                            {/* Hashtags */}
                            <div className="pm-card-hashtags-wrap">
                              {service.tags.map((tag, tIdx) => (
                                <span key={tIdx} className="pm-hash-chip">
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Card Action Footer */}
                          <div className="pm-card-action-footer">
                            <button
                              type="button"
                              className="pm-card-find-btn"
                              onClick={() => handleExplore(service)}
                            >
                              <span>{service.btnText}</span>
                              <div className="pm-btn-arrow-circle">
                                <i className="fas fa-arrow-right"></i>
                              </div>
                            </button>

                            {/* <Link
                              to="/register"
                              className="pm-card-book-link"
                              title="Book a direct session"
                            >
                              Book Intake
                            </Link> */}
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            );
          })
        )}

        {/* =========================================================
            BOTTOM CALL TO ACTION BANNER
            ========================================================= */}
        <section className="pm-services-bottom-cta">
          <div className="pm-cta-orb-bg"></div>
          <div className="pm-cta-inner-card">
            <h2 className="pm-cta-title">
              Still not sure? That's okay.
            </h2>
            <p className="pm-cta-subtext">
              You don't have to know exactly who you're looking for. Explore your options,
              learn about different professionals, and take the time to choose what feels right
              for you.
            </p>
            <div className="pm-cta-buttons-wrap">
              <Link to="/therapists" className="pm-btn-primary-hero">
                Explore All Professionals <i className="fas fa-arrow-right ms-2"></i>
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default ServicesPage;
